import type { EChartsOption } from "echarts";

import { escapeHtml } from "./chartTooltip";

/**
 * A sankey: one metric flowing from the first dimension's values to the
 * second's (plan 2026-10-05 finance chart types, C3 / D5).
 *
 * The rows arrive as EDGES, unpivoted: the registry row says its second
 * dimension is structural, so no writer stamps `breakdown_key` and the
 * response chokepoint hands the long rows over as they came. The edge
 * direction is the IR's dimension order (`dimension_keys[0]` = from,
 * `[1]` = to); there is no second spec key for it.
 *
 * Kept out of `optionBuilder.ts` like the KPI pictures, so the shared builder
 * only carries the recipe that dispatches here.
 */

/** What the sankey reads; a structural subset of the option builder's input. */
export type SankeyInput = {
  rows: Record<string, unknown>[];
  yKeys: string[];
  labels: Record<string, string> | undefined;
  /** The colour of one node, by its id and its position in the node list.
   *  The option builder answers it: by the node's series ref on a dashboard
   *  (`seriesColors.sankeyNodeRef`), by position everywhere else. This module
   *  never indexes a palette itself (ADR-0476). */
  nodeColor: (id: string, index: number) => string | undefined;
  /** The first dimension's RAW value per row: the flow's "from". A node's
   *  identity, and with it its colour (`seriesColors.sankeyNodeRef`), is the
   *  raw value; a formatted date would key it apart from the pane's ref. */
  categories: string[];
  /** The first dimension's display label per row (a formatted date), aligned
   *  with `categories`. Drawn as the node's label and in the tooltip only.
   *  Absent, the raw value is the label. */
  categoryLabels?: string[];
  /** The second dimension's value per row: the flow's "to". Empty when the
   *  spec names fewer than two dimensions. */
  secondCategories: string[];
  formatValue: (value: number) => string;
  animation: Record<string, unknown>;
};

/** Nodes kept per side; the rest fold into that side's "Other". */
export const SANKEY_MAX_NODES_PER_SIDE = 10;

const NOT_SET = "(not set)";
const OTHER = "Other";
type Side = "s" | "t";

/**
 * A node's id carries its SIDE, so a value present on both sides (billing
 * country → shipping country, NL → NL) is two nodes and the graph is
 * bipartite: a cycle, which ECharts refuses to lay out, cannot be formed. The
 * "Other" id cannot collide with a real value called "Other", because a real
 * value's id is `s:` + the value and this one has no such spelling.
 */
const nodeId = (side: Side, name: string) => `${side}:${name}`;
const otherId = (side: Side) => `${side}\u0000other`;

/** What a node id reads as: the side's "Other", the display label of a
 *  source value when the caller gave one, otherwise the value itself. */
function nodeLabeller(input: Pick<SankeyInput, "categories" | "categoryLabels">): (id: string) => string {
  const shown = new Map<string, string>();
  input.categories.forEach((raw, i) => {
    const label = input.categoryLabels?.[i];
    if (raw && label != null && label !== raw) shown.set(nodeId("s", raw), label);
  });
  return (id) => {
    if (id.endsWith("\u0000other")) return OTHER;
    return shown.get(id) ?? id.slice(2);
  };
}

const finiteNumber = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

export type SankeyEdge = { source: string; target: string; value: number };

/**
 * The edges a sankey draws, folded: per side the `SANKEY_MAX_NODES_PER_SIDE`
 * nodes with the most flow keep their name and the rest become that side's
 * "Other". Flows sum, so the fold is honest: the total is the same before and
 * after, and two edges that now share both ends are merged.
 *
 * A zero flow is dropped (there is no link to draw) and a negative one is
 * reported in `negatives`, because a band cannot have a negative width.
 */
export function sankeyEdges(input: Pick<SankeyInput, "rows" | "yKeys" | "categories" | "secondCategories">): {
  edges: SankeyEdge[];
  negatives: number;
} {
  const key = input.yKeys[0];
  const raw: SankeyEdge[] = [];
  let negatives = 0;
  if (key == null || input.secondCategories.length === 0) return { edges: [], negatives };
  input.rows.forEach((row, i) => {
    const value = finiteNumber(row[key]);
    if (value == null || value === 0) return;
    if (value < 0) {
      negatives += 1;
      return;
    }
    raw.push({
      source: nodeId("s", input.categories[i] || NOT_SET),
      target: nodeId("t", input.secondCategories[i] || NOT_SET),
      value,
    });
  });

  const keep = (side: "source" | "target", other: string): ((id: string) => string) => {
    const flow = new Map<string, number>();
    for (const e of raw) flow.set(e[side], (flow.get(e[side]) ?? 0) + e.value);
    if (flow.size <= SANKEY_MAX_NODES_PER_SIDE) return (id) => id;
    const kept = new Set(
      [...flow.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, SANKEY_MAX_NODES_PER_SIDE - 1)
        .map(([id]) => id),
    );
    return (id) => (kept.has(id) ? id : other);
  };
  const fromOf = keep("source", otherId("s"));
  const toOf = keep("target", otherId("t"));

  const merged = new Map<string, SankeyEdge>();
  for (const e of raw) {
    const source = fromOf(e.source);
    const target = toOf(e.target);
    const k = `${source}\u0001${target}`;
    const prior = merged.get(k);
    if (prior) prior.value += e.value;
    else merged.set(k, { source, target, value: e.value });
  }
  return { edges: [...merged.values()], negatives };
}

/** Grey that reads on both the light and the dark card: a dashboard tile
 *  hands ECharts no theme (D-2 gate, 2026-10-06: `#666` vanished on dark). */
const MUTED = "#909296";

export function buildSankeyOption(input: SankeyInput): EChartsOption {
  const { edges, negatives } = sankeyEdges(input);
  const labelOf = nodeLabeller(input);
  const key = input.yKeys[0];
  const metricLabel = key ? input.labels?.[key] ?? key : "";

  // Why there is no picture, in the reader's words. A negative flow refuses
  // the whole chart rather than drawing the rest: a sankey's bands are shares
  // of the same flow, so leaving one out would misstate every other band.
  const refusal =
    negatives > 0
      ? {
          text: negatives === 1 ? "One flow has a negative value" : `${negatives} flows have a negative value`,
          // Line breaks written out: a wrap by `width` depends on the
          // font's measured widths, which differ between the CI runners and
          // a laptop, and so did the golden picture (treemap_refused has
          // the same cause and is skipped for it).
          subtext: "A sankey draws each flow as a band,\nso every value has to be zero or higher.",
        }
      : edges.length === 0
        ? { text: "Nothing to show", subtext: "No flows between the two dimensions\nin this period." }
        : null;
  if (refusal) {
    return {
      ...input.animation,
      title: {
        ...refusal,
        left: "center",
        top: "middle",
        textStyle: { fontSize: 13, fontWeight: "normal" as const, color: MUTED },
        subtextStyle: { fontSize: 11, color: "#94a3b8", lineHeight: 15 },
      },
      series: [],
    } as EChartsOption;
  }

  const ids = [...new Set(edges.flatMap((e) => [e.source, e.target]))];
  const nodes = ids.map((id, i) => ({
    name: id,
    itemStyle: { color: input.nodeColor(id, i) },
    // Labels face inward, so neither column's names run off the tile.
    label: { position: id.startsWith("s") ? ("right" as const) : ("left" as const) },
  }));

  return {
    ...input.animation,
    tooltip: {
      trigger: "item",
      confine: true,
      // Rendered as HTML, and every node name is customer data.
      formatter: (params: any) => {
        if (params?.dataType === "edge") {
          const d = params.data ?? {};
          return [
            `${escapeHtml(labelOf(String(d.source ?? "")))} → ${escapeHtml(labelOf(String(d.target ?? "")))}`,
            `${escapeHtml(metricLabel)}: ${escapeHtml(input.formatValue(Number(d.value ?? 0)))}`,
          ].join("<br/>");
        }
        return [
          `<strong>${escapeHtml(labelOf(String(params?.name ?? "")))}</strong>`,
          `${escapeHtml(metricLabel)}: ${escapeHtml(input.formatValue(Number(params?.value ?? 0)))}`,
        ].join("<br/>");
      },
    },
    series: [
      {
        type: "sankey",
        left: 8,
        right: 8,
        top: 8,
        bottom: 8,
        nodeWidth: 12,
        nodeGap: 8,
        // A two-column graph: the layout iterations only reorder within a
        // column, so the default is plenty and stays deterministic.
        layoutIterations: 32,
        emphasis: { focus: "adjacency" },
        lineStyle: { color: "gradient", opacity: 0.35, curveness: 0.5 },
        // A label is drawn as text, not HTML, so it is not escaped (that
        // would show a literal `&amp;`); the tooltip above is.
        // A fixed mid grey and no text border: ECharts' default sankey label
        // is drawn with an outline, which doubled every name on the dark
        // card (D-2 gate).
        label: {
          fontSize: 11,
          color: MUTED,
          textBorderWidth: 0,
          formatter: (p: any) => labelOf(String(p?.name ?? "")),
        },
        data: nodes,
        links: edges,
      },
    ],
  } as EChartsOption;
}
